/**
 * Favorites Module — localStorage-based favorites
 *
 * Stores favorite frame configurations in localStorage.
 * No Storefront API or customer access token needed.
 *
 * Dependencies: none (standalone module)
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'framelin_favorites';

  /* ===================================================================
   * HELPERS
   * =================================================================== */

  function generateId() {
    return Date.now().toString(36) + '-' + Math.random().toString(36).substr(2, 6);
  }

  function readFavorites() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      return JSON.parse(raw);
    } catch (e) {
      return [];
    }
  }

  function writeFavoritesLocal(favorites) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
    } catch (e) {
      console.warn('[Favorites] localStorage write error:', e);
    }
  }

  /* ===================================================================
   * FAVORITES CRUD
   * =================================================================== */

  /**
   * Read all favorites from localStorage.
   * Returns a Promise for API compatibility with existing code.
   */
  function getAllFavorites() {
    return Promise.resolve(readFavorites());
  }

  /**
   * Save a new favorite.
   * @param {Object} favoriteData - { productHandle, productTitle, configUrl, frameSpec }
   * @returns {Promise<Object>} The saved favorite with id and savedAt
   */
  function saveFavorite(favoriteData) {
    var favorites = readFavorites();

    // Duplicate check: same config URL
    var isDuplicate = favorites.some(function (f) {
      return f.configUrl === favoriteData.configUrl;
    });
    if (isDuplicate) {
      return Promise.reject(new Error('duplicate'));
    }

    var newFavorite = {
      id: generateId(),
      savedAt: new Date().toISOString(),
      productHandle: favoriteData.productHandle,
      productTitle: favoriteData.productTitle,
      configUrl: favoriteData.configUrl,
      frameSpec: favoriteData.frameSpec
    };

    favorites.unshift(newFavorite);

    // Keep max 50 items
    if (favorites.length > 50) {
      favorites = favorites.slice(0, 50);
    }

    writeFavoritesLocal(favorites);
    return Promise.resolve(newFavorite);
  }

  /**
   * Remove a favorite by ID.
   */
  function removeFavorite(favoriteId) {
    var favorites = readFavorites();
    var filtered = favorites.filter(function (f) {
      return f.id !== favoriteId;
    });
    writeFavoritesLocal(filtered);
    return Promise.resolve(filtered);
  }

  /**
   * Check if a specific config URL is already favorited.
   */
  function isFavorited(configUrl) {
    var favorites = readFavorites();
    var found = favorites.some(function (f) {
      return f.configUrl === configUrl;
    });
    return Promise.resolve(found);
  }

  /* ===================================================================
   * PUBLIC API
   * =================================================================== */

  window.FramelinFavorites = {
    getAllFavorites: getAllFavorites,
    saveFavorite: saveFavorite,
    removeFavorite: removeFavorite,
    isFavorited: isFavorited
  };
})();
